import { useEffect, useState } from "react";
import FlatRatePlan from "./FlatRatePlan";
import InterchangePlusPlan from "./InterchangePlusPlan";
import { AccountHolderRatePlan } from "../../services/accountService";
import { useSnackbar } from "../../components/Snackbar";
import Switch from "../../components/Switch";
import useCurrentUserAccount from "../../hooks/data/queries/useCurrentUserAccount";
import Panel from "./Panel";
import Tab from "../../components/Tab";
import ChangePlanConfirmationModal from "./ChangePlanConfirmationModal";
import { csrAdminRoles } from "../../components/HasAccess";
import { displayWalletRates } from "../../utils/walletRates";

export type RatePlan = {
  id: string;
  label: string;
  data?: AccountHolderRatePlan;
};

let ratePlanTypes = ["Flat Rate Plan"];
ratePlanTypes.push("Interchange ++ Plan");

export type RatePlanType = typeof ratePlanTypes[number];

const countriesThatAllowInterchange = ["US", "PR", "MP", "UM", "VI"];

type Props = {
  accountHolderCode: string;
  accountHolderId: string;
  accountHolderRatePlans: AccountHolderRatePlan[];
  useInterchangeRate: boolean;
  hasMonthlyFee: boolean;
  monthlyFee: string;
  isMasterSurchargeEnabled: boolean;
  inBalancePlatform: boolean;
  useTechFee: boolean;
  useConvFee: boolean;
  country: string;
  isLoading: boolean;
};

function RatePlans({
  accountHolderCode,
  accountHolderId,
  accountHolderRatePlans,
  useInterchangeRate,
  hasMonthlyFee,
  monthlyFee,
  isMasterSurchargeEnabled,
  inBalancePlatform,
  useTechFee,
  useConvFee,
  country,
  isLoading,
}: Props): JSX.Element {
  // Initialize ratePlans locally
  let ratePlans: RatePlan[] = [
    { id: "visa", label: "Visa" },
    { id: "mc", label: "Mastercard" },
    { id: "amex", label: "AMEX" },
    { id: "discover", label: "Discover" },
    { id: "ach", label: "ACH" },
  ];

  // Pass country to displayWalletRates
  ratePlans = displayWalletRates(ratePlans, country);

  // Add JCB and EFTPOS to rate plans if country is AU and they are not already in the list
  if (country === "AU") {
    if (!ratePlans.some((plan) => plan.id === "jcb")) {
      ratePlans.push({ id: "jcb", label: "JCB" });
    }
    if (!ratePlans.some((plan) => plan.id === "eftpos_australia")) {
      ratePlans.push({ id: "eftpos_australia", label: "EFTPOS" });
    }
  }

  const { data: currentUserAccount } = useCurrentUserAccount();
  const isCSRAdmin = currentUserAccount?.role
    ? csrAdminRoles.includes(currentUserAccount?.role)
    : false;
  const [tabIndex, setTabIndex] = useState(-1);
  const [showConfirmChangeModal, setShowConfirmChangeModal] = useState(false);
  const { openSnackbar } = useSnackbar({
    duration: 30000,
    position: "top",
  });

  useEffect(() => {
    setTabIndex(useInterchangeRate ? 1 : 0);
  }, [useInterchangeRate]);

  function handlePlanChanged(useInterchangeValue: boolean) {
    const newTabIndex = useInterchangeValue ? 1 : 0;
    setTabIndex(newTabIndex);
    setShowConfirmChangeModal(false);
    openSnackbar(`${ratePlanTypes[newTabIndex]} selected.`, {
      type: "success",
      duration: 5000,
    });
  }

  function handlePlanChangeError() {
    setShowConfirmChangeModal(false);
    openSnackbar("Failed to change rate plan type.", {
      type: "error",
      duration: 5000,
    });
  }

  function handlePlanChange() {
    if (useConvFee || useTechFee) {
      openSnackbar(
        `Account Holder ${accountHolderCode} has Surcharge or Tech Fee on and Interchange++ does not apply.`,
        {
          type: "error",
          duration: 5000,
        }
      );
      return;
    }

    if (isCSRAdmin && inBalancePlatform) {
      setShowConfirmChangeModal(true);
    }
  }

  function handleTabChange(index = 0) {
    setTabIndex(index);
  }

  return (
    <>
      <Panel className="col-span-7">
        <Panel.Header title="Rate Plans">
          <div>
            {inBalancePlatform &&
              countriesThatAllowInterchange.includes(country) && (
                <Switch
                  checked={Boolean(useInterchangeRate)}
                  onChange={() => {
                    handlePlanChange();
                  }}
                  disabled={!isCSRAdmin}
                  label="Interchange ++"
                  labelProps={{ className: "font-medium w-100 mr-2" }}
                />
              )}
          </div>
        </Panel.Header>
        <Panel.Content>
          {inBalancePlatform &&
          countriesThatAllowInterchange.includes(country) ? (
            <Tab.Group selectedIndex={tabIndex} onChange={handleTabChange}>
              <Tab.List>
                {ratePlanTypes.map((type, i) => (
                  <Tab
                    key={i}
                    index={i}
                    disabled={!isCSRAdmin}
                    isLoading={isLoading}
                  >
                    {type}{" "}
                    {i === (useInterchangeRate ? 1 : 0) ? "(ACTIVE)" : ""}
                  </Tab>
                ))}
              </Tab.List>
              <Tab.Panels>
                <FlatRatePlan
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountHolderId}
                  accountHolderRatePlans={accountHolderRatePlans}
                  useInterchangeRate={useInterchangeRate}
                  ratePlanType="Flat Rate Plan"
                  isMasterSurchargeEnabled={isMasterSurchargeEnabled}
                  isLoading={isLoading}
                  ratePlans={ratePlans}
                />
                <InterchangePlusPlan
                  accountHolderCode={accountHolderCode}
                  accountHolderId={accountHolderId}
                  accountHolderRatePlans={accountHolderRatePlans}
                  useInterchangeRate={useInterchangeRate}
                  isLoading={isLoading}
                  hasMonthlyFee={hasMonthlyFee}
                  ratePlanType="Interchange ++ Plan"
                  monthlyFee={monthlyFee}
                  ratePlans={ratePlans}
                />
              </Tab.Panels>
            </Tab.Group>
          ) : (
            <div className="flex flex-col">
              <h3 className="font-semibold">Flat Rate Plan</h3>
              <FlatRatePlan
                accountHolderCode={accountHolderCode}
                accountHolderId={accountHolderId}
                accountHolderRatePlans={accountHolderRatePlans}
                useInterchangeRate={useInterchangeRate}
                ratePlanType="Flat Rate Plan"
                isMasterSurchargeEnabled={isMasterSurchargeEnabled}
                isLoading={isLoading}
                ratePlans={ratePlans}
              />
            </div>
          )}
        </Panel.Content>
      </Panel>
      <ChangePlanConfirmationModal
        isOpen={showConfirmChangeModal}
        accountHolderCode={accountHolderCode}
        useInterchangeRate={useInterchangeRate}
        onPlanChanged={handlePlanChanged}
        onError={handlePlanChangeError}
        onClose={() => setShowConfirmChangeModal(false)}
      />
    </>
  );
}

export default RatePlans;
